import {getAbiForAsset} from 'src/utils/getAbiForAsset';
import {apiAmountToAssetAmount} from 'src/utils/helpers';
import utf8ToHex from 'src/utils/utf8ToHex';
import type {SupportedCurrency, TransactionDetails} from 'src/v2/state/types';
import type {EvmWriteContractData, SolanaTransactionData} from 'src/types';
import {getDepositAddress} from 'src/v2/components/pay/getDepositAddress';

export const getEvmTransactionData = ({
  selectedCurrency,
  amount,
  depositAddresses,
  memoMessage,
  sourceCurrency = 'usd',
}: {
  selectedCurrency: SupportedCurrency;
  amount: number;
  depositAddresses: Record<string, string>;
  memoMessage: string;
  sourceCurrency?: string;
}): EvmWriteContractData => {
  const depositAddress = getDepositAddress({
    selectedAsset: selectedCurrency,
    depositAddresses,
  }) as `0x${string}`;

  const abi = getAbiForAsset(selectedCurrency);
  // convert ts number -> bigint
  // Note: when testing on dev you can set this to 0 as a hack
  const valueBigint = apiAmountToAssetAmount(
    amount,
    sourceCurrency,
    selectedCurrency.currencyMinorUnits,
  );
  return {
    abi,
    address: selectedCurrency.contractAddress as `0x${string}`,
    functionName: 'transfer',
    args: [depositAddress, valueBigint],
    dataSuffix: utf8ToHex(memoMessage),
  };
};

export const getSolanaTransactionData = ({
  selectedCurrency,
  amount,
  depositAddresses,
  memoMessage,
  transactionDetails,
  sourceCurrency = 'usd',
}: {
  selectedCurrency: SupportedCurrency;
  amount: number;
  depositAddresses: Record<string, string>;
  memoMessage: string;
  transactionDetails: TransactionDetails;
  sourceCurrency?: string;
}): SolanaTransactionData => {
  const depositAddress = getDepositAddress({
    selectedAsset: selectedCurrency,
    depositAddresses,
  });

  const tokenDepositAmount = apiAmountToAssetAmount(
    amount,
    sourceCurrency,
    selectedCurrency.currencyMinorUnits,
  );

  if (!transactionDetails?.solanaLatestBlockhash) {
    throw new Error(
      'Missing Solana transaction details: latest blockhash not found',
    );
  }

  if (!transactionDetails.walletAddress) {
    throw new Error(
      'Missing Solana transaction details: wallet address not found',
    );
  }

  return {
    destinationAddress: depositAddress,
    destinationTokenAccountAddress:
      transactionDetails.solanaDestinationTokenAccountAddress || null,
    currencyContractAddress: selectedCurrency.contractAddress,
    amount: tokenDepositAmount,
    memoMessage,
    latestBlockhash: transactionDetails.solanaLatestBlockhash,
    walletAddress: transactionDetails.walletAddress,
    currency: selectedCurrency.assetCode.toString(),
  };
};
